import type { ReactNode } from "react";
import type { PartnerFeatures } from "../config/schema";
import type { PaymentMethod } from "../utils/paymentMethods";
import { useBranding } from "./BrandingProvider";
import { resolvePaymentMethods } from "./usePaymentMethods";

// Two distinct sources of truth — features (boolean partner flags) and
// payments (composes partner-level gate with per-user credit-card gate).
// Modeled as a discriminated union so each variant carries only its relevant
// props and TS rules out invalid combinations at the callsite.
type FeatureGateProps = (
  | { feature: keyof PartnerFeatures }
  | {
      payment: PaymentMethod;
      creditCardAllowedForUser?: boolean;
      overpayAllowedForUser?: boolean;
    }
) & {
  children: ReactNode;
  // Rendered when the gate is closed. Defaults to nothing (the common case
  // for hiding optional UI rows / panels).
  fallback?: ReactNode;
};

// `<FeatureGate feature="shopify">…</FeatureGate>` — partner feature flag
// `<FeatureGate payment="credit_card" creditCardAllowedForUser={user.cc_allowed}>…</FeatureGate>` — payment availability
//
// Centralizes the "if (gateOpen) <X />" pattern so the gate logic lives in
// one place. No negation prop — if you need to show something *because* a
// feature is off, use `fallback` (cleaner than spreading negation through
// the component tree).
export const FeatureGate = (props: FeatureGateProps) => {
  const branding = useBranding();
  const isOpen =
    "feature" in props
      ? branding.features[props.feature]
      : resolvePaymentMethods(branding.payments, {
          creditCardAllowedForUser: props.creditCardAllowedForUser,
          overpayAllowedForUser: props.overpayAllowedForUser,
        }).available.includes(props.payment);

  return <>{isOpen ? props.children : (props.fallback ?? null)}</>;
};
